import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  TextInput,
  Loader,
  CloseButton,
  Stack,
  Group,
  Text,
  UnstyledButton,
  ScrollArea,
  Box,
} from "@mantine/core";
import { IconMapPin, IconCurrentLocation, IconSearch } from "@tabler/icons-react";
import { useAddressAutocomplete } from "@/hooks/useAddressAutocomplete";
import { useLocationHistoryContext } from "@/contexts";
import { GeoCoords } from "@/hooks/useGeolocation";
import { AddressData } from "@/hooks/useGoogleMapsAutocomplete";

// ============================================
// Constants
// ============================================

const MIN_SEARCH_LENGTH = 3;
const MIN_GEOCODE_LENGTH = 5;

/** Delay for auto-focus after fullscreen modal animation completes */
const AUTOFOCUS_DELAY_MS = 300;

// ============================================
// Types
// ============================================

export interface AddressPickerModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Callback to close the modal */
  onClose: () => void;

  /** Callback when an address is selected */
  onSelect: (address1: string, fullAddress: AddressData | null) => void;

  /** Current input value to pre-populate search */
  initialValue?: string;

  /** Custom addresses to show (e.g., saved addresses) */
  customAddresses?: AddressData[];

  /** Location bias for relevant nearby results */
  locationBias?: GeoCoords | null;

  /** Placeholder text */
  placeholder?: string;

  /** Modal title */
  title?: string;

  /** Whether to show the "Use my location" button */
  showLocateButton?: boolean;

  /** Tooltip text for locate button */
  locateButtonTooltip?: string;

  /** Callback to request geolocation (fired when modal opens to get location bias early) */
  onRequestGeolocation?: () => void;
}

// ============================================
// Component
// ============================================

export const AddressPickerModal: React.FC<AddressPickerModalProps> = ({
  opened,
  onClose,
  onSelect,
  initialValue = "",
  customAddresses = [],
  locationBias = null,
  placeholder = "Search for an address",
  title = "Select Address",
  showLocateButton = true,
  locateButtonTooltip = "Use my current location",
  onRequestGeolocation,
}) => {
  const {
    addressPredictions,
    loading,
    fetchAddressPredictions,
    fetchAddressDetails,
    geocodeAddress,
    clearAddressPredictions,
  } = useAddressAutocomplete();

  const {
    requestLocation,
    isLoading: locationLoading,
    permissionDenied,
    isSupported: geoSupported,
  } = useLocationHistoryContext();

  const [searchValue, setSearchValue] = useState("");
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isLocating, setIsLocating] = useState(false);

  /**
   * Track whether the user has already confirmed an address via selection
   * (prediction pick, custom address pick, locate button, or "Use ..." button).
   * If true, the close handler should NOT forward-geocode again.
   */
  const hasConfirmedRef = useRef(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const locationBiasRef = useRef(locationBias);
  const searchValueRef = useRef("");

  useEffect(() => {
    locationBiasRef.current = locationBias;
  }, [locationBias]);

  // Keep searchValueRef in sync for use in the close handler
  useEffect(() => {
    searchValueRef.current = searchValue;
  }, [searchValue]);

  // Initialize search value when modal opens
  useEffect(() => {
    if (opened) {
      hasConfirmedRef.current = false;
      setSearchValue(initialValue);
      if (initialValue && initialValue.trim().length >= MIN_SEARCH_LENGTH) {
        fetchAddressPredictions(initialValue, locationBiasRef.current);
      }

      // Request geolocation early so location bias is available for suggestions
      onRequestGeolocation?.();

      // Focus input after fullscreen modal animation completes
      setTimeout(() => {
        inputRef.current?.focus();
      }, AUTOFOCUS_DELAY_MS);
    } else {
      setSearchValue("");
      clearAddressPredictions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  /**
   * Handle modal close — forward geocode if user typed without selecting.
   *
   * This covers the case where the user types an address in the search field
   * then dismisses the modal (via X button, swipe-back, or backdrop tap)
   * without picking a suggestion or tapping "Use ...".
   */
  const handleClose = useCallback(async () => {
    // If user already confirmed via selection, just close
    if (hasConfirmedRef.current) {
      onClose();
      return;
    }

    const currentSearch = searchValueRef.current?.trim() || "";

    // If search is empty or unchanged from initial value, just close (no changes)
    if (!currentSearch || currentSearch === initialValue?.trim()) {
      onClose();
      return;
    }

    // If user typed something new, attempt forward geocode before closing
    if (currentSearch.length >= MIN_GEOCODE_LENGTH) {
      setIsLoadingDetails(true);

      try {
        const result = await geocodeAddress(currentSearch);
        onSelect(currentSearch, result);
      } catch (err) {
        console.error("Forward geocode on modal close failed:", err);
        onSelect(currentSearch, null);
      } finally {
        setIsLoadingDetails(false);
        onClose();
      }
    } else {
      // Too short for geocoding — pass as-is with null coordinates
      onSelect(currentSearch, null);
      onClose();
    }
  }, [initialValue, geocodeAddress, onSelect, onClose]);

  /**
   * Handle search input change
   */
  const handleSearchChange = useCallback((newValue: string) => {
    setSearchValue(newValue);

    if (newValue && newValue.trim().length >= MIN_SEARCH_LENGTH) {
      fetchAddressPredictions(newValue, locationBiasRef.current);
    } else {
      clearAddressPredictions();
    }
  }, [fetchAddressPredictions, clearAddressPredictions]);

  /**
   * Handle clear search
   */
  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    clearAddressPredictions();
    inputRef.current?.focus();
  }, [clearAddressPredictions]);

  /**
   * Handle custom address selection
   */
  const handleCustomAddressSelect = useCallback((address: AddressData) => {
    hasConfirmedRef.current = true;
    const displayValue = address.address1 || "";
    onSelect(displayValue, address);
    onClose();
  }, [onSelect, onClose]);

  /**
   * Handle Google prediction selection
   */
  const handleGooglePredictionSelect = useCallback(async (
    addressId: string,
    displayName: string
  ) => {
    setIsLoadingDetails(true);

    try {
      const fullAddress = await fetchAddressDetails(addressId);

      hasConfirmedRef.current = true;
      if (fullAddress) {
        onSelect(fullAddress.address1 || displayName, fullAddress);
      } else {
        onSelect(displayName, null);
      }
    } catch (err) {
      console.error("Failed to fetch address details:", err);
      onSelect(displayName, null);
    } finally {
      setIsLoadingDetails(false);
      onClose();
    }
  }, [fetchAddressDetails, onSelect, onClose]);

  /**
   * Handle "Use my location" button click
   */
  const handleLocateClick = useCallback(async () => {
    setIsLocating(true);

    try {
      const result = await requestLocation({
        reverseGeocode: true,
        allowPrompt: true,
      });

      if (result?.address) {
        hasConfirmedRef.current = true;
        const displayValue = result.address.displayName || result.address.address1 || "";
        onSelect(displayValue, result.address);
        onClose();
      }
    } catch (err) {
      console.error("Failed to get location:", err);
    } finally {
      setIsLocating(false);
    }
  }, [requestLocation, onSelect, onClose]);

  /**
   * Handle confirming manually typed address (forward geocode)
   * — the explicit "Use ..." button at the bottom of the list.
   */
  const handleConfirmManualAddress = useCallback(async () => {
    if (!searchValue || searchValue.trim().length < MIN_GEOCODE_LENGTH) {
      hasConfirmedRef.current = true;
      onSelect(searchValue, null);
      onClose();
      return;
    }

    setIsLoadingDetails(true);

    try {
      const result = await geocodeAddress(searchValue);
      hasConfirmedRef.current = true;
      onSelect(searchValue, result);
    } catch (err) {
      console.error("Forward geocode failed:", err);
      hasConfirmedRef.current = true;
      onSelect(searchValue, null);
    } finally {
      setIsLoadingDetails(false);
      onClose();
    }
  }, [searchValue, geocodeAddress, onSelect, onClose]);

  const hasCustomAddresses = customAddresses.length > 0;
  const hasGooglePredictions = addressPredictions.length > 0;
  const hasSuggestions = hasCustomAddresses || hasGooglePredictions;
  const isLoading = loading || isLoadingDetails || isLocating;
  const canShowLocateButton = showLocateButton && geoSupported && !permissionDenied;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={title}
      fullScreen
      withCloseButton
      styles={{
        body: { padding: 0, display: "flex", flexDirection: "column", height: "calc(100vh - 60px)" },
        header: { padding: "12px 16px" },
      }}
    >
      {/* Search Input */}
      <Box px="md" pt="xs" pb="sm">
        <TextInput
          ref={inputRef}
          placeholder={placeholder}
          value={searchValue}
          onChange={(event) => handleSearchChange(event.currentTarget.value)}
          leftSection={<IconSearch size={20} />}
          rightSection={
            isLoading ? (
              <Loader size={20} />
            ) : searchValue ? (
              <CloseButton
                size="md"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleClearSearch}
                aria-label="Clear search"
              />
            ) : null
          }
          size="lg"
        />

        {/* Use my location button — large touch target */}
        {canShowLocateButton && (
          <UnstyledButton
            onClick={handleLocateClick}
            mt="sm"
            w="100%"
            py="md"
            px="sm"
            style={{ borderRadius: "var(--mantine-radius-sm)", minHeight: 48 }}
          >
            <Group gap="sm">
              <IconCurrentLocation size={24} />
              <Text size="md" fw={500}>{locateButtonTooltip}</Text>
              {(isLocating || locationLoading) && <Loader size={18} />}
            </Group>
          </UnstyledButton>
        )}
      </Box>

      {/* Results */}
      <ScrollArea flex={1} px="md">
        <Stack gap={0}>
          {/* No results message */}
          {!hasSuggestions && searchValue.length >= MIN_SEARCH_LENGTH && !loading && (
            <Text c="dimmed" ta="center" py="xl" size="md">
              No addresses found
            </Text>
          )}

          {/* Type more characters hint */}
          {!hasSuggestions && searchValue.length > 0 && searchValue.length < MIN_SEARCH_LENGTH && !loading && (
            <Text c="dimmed" ta="center" py="xl" size="md">
              Type at least {MIN_SEARCH_LENGTH} characters to search
            </Text>
          )}

          {/* Custom / Saved Addresses */}
          {hasCustomAddresses && (
            <>
              <Text size="sm" fw={600} c="dimmed" tt="uppercase" py="xs">
                Saved Addresses
              </Text>
              {customAddresses.map((address) => (
                <UnstyledButton
                  key={address.addressId}
                  onClick={() => handleCustomAddressSelect(address)}
                  py="md"
                  px="sm"
                  w="100%"
                  style={{ borderRadius: "var(--mantine-radius-sm)", minHeight: 48 }}
                >
                  <Group gap="sm" wrap="nowrap" align="flex-start">
                    <IconMapPin size={22} style={{ flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <Text size="md" fw={500}>
                        {address.displayName || address.address1}
                      </Text>
                      {address.address1 && address.displayName !== address.address1 && (
                        <Text size="sm" c="dimmed">
                          {address.address1}
                          {address.city && `, ${address.city}`}
                          {address.region && `, ${address.region}`}
                        </Text>
                      )}
                    </div>
                  </Group>
                </UnstyledButton>
              ))}
            </>
          )}

          {/* Google Predictions */}
          {hasGooglePredictions && (
            <>
              {hasCustomAddresses && (
                <Text size="sm" fw={600} c="dimmed" tt="uppercase" py="xs" mt="xs">
                  Search Results
                </Text>
              )}
              {addressPredictions.map((prediction) => (
                <UnstyledButton
                  key={prediction.addressId}
                  onClick={() =>
                    handleGooglePredictionSelect(
                      prediction.addressId,
                      prediction.displayName
                    )
                  }
                  py="md"
                  px="sm"
                  w="100%"
                  style={{ borderRadius: "var(--mantine-radius-sm)", minHeight: 48 }}
                >
                  <Group gap="sm" wrap="nowrap">
                    <IconMapPin size={22} style={{ flexShrink: 0 }} />
                    <Text size="md">{prediction.displayName}</Text>
                  </Group>
                </UnstyledButton>
              ))}
            </>
          )}

          {/* Use typed address as-is */}
          {searchValue.trim().length >= MIN_GEOCODE_LENGTH && !isLoadingDetails && (
            <UnstyledButton
              onClick={handleConfirmManualAddress}
              py="md"
              px="sm"
              mt="xs"
              w="100%"
              style={{ borderRadius: "var(--mantine-radius-sm)", minHeight: 48 }}
            >
              <Group gap="sm" wrap="nowrap">
                <IconSearch size={22} style={{ flexShrink: 0 }} />
                <Text size="md" c="dimmed">
                  Use "{searchValue.trim()}"
                </Text>
              </Group>
            </UnstyledButton>
          )}
        </Stack>
      </ScrollArea>
    </Modal>
  );
};